import React from 'react';
import { LoaderCircle, Plus, Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/Modules/Core/Hooks/useDebouncedValue';
import {
  TAG_BADGE_CLASS_NAME,
  getTagKey,
  mergeTagOptions,
  normalizeTagList,
  normalizeTagName,
} from '@/Modules/Core/Components/Tags/tagUtils';

const createTagOption = (value) => ({
  value,
  label: value,
});

const TagInput = ({
  value = [],
  options = [],
  onChange,
  onSearchChange,
  placeholder = 'Add tags',
  searchPlaceholder = 'Search tags',
  emptyMessage = 'No tags found',
  createMessage = 'Create',
  disabled = false,
  loading = false,
  allowCreate = true,
  maxHeight = '260px',
  className,
  controlClassName,
}) => {
  const containerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const [query, setQuery] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const selectedTags = React.useMemo(() => normalizeTagList(value), [value]);
  const availableOptions = React.useMemo(
    () => mergeTagOptions(options, selectedTags),
    [options, selectedTags]
  );
  const debouncedQuery = useDebouncedValue(query, 300);

  React.useEffect(() => {
    onSearchChange?.(debouncedQuery.trim());
  }, [debouncedQuery, onSearchChange]);

  React.useEffect(() => {
    const handlePointerDown = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  const selectedKeys = React.useMemo(
    () => new Set(selectedTags.map((tag) => getTagKey(tag.value || tag.label))),
    [selectedTags]
  );
  const normalizedQuery = normalizeTagName(query);
  const normalizedQueryKey = getTagKey(normalizedQuery);

  const suggestionOptions = React.useMemo(() => {
    if (!normalizedQuery) {
      return availableOptions.filter(
        (tag) => !selectedKeys.has(getTagKey(tag.value || tag.label))
      );
    }

    return availableOptions.filter((tag) => {
      const key = getTagKey(tag.value || tag.label);

      if (selectedKeys.has(key)) {
        return false;
      }

      return `${tag.label} ${tag.value}`
        .toLowerCase()
        .includes(normalizedQuery.toLowerCase());
    });
  }, [availableOptions, normalizedQuery, selectedKeys]);

  const canCreate =
    allowCreate &&
    !disabled &&
    normalizedQuery &&
    !selectedKeys.has(normalizedQueryKey) &&
    !availableOptions.some(
      (tag) => getTagKey(tag.value || tag.label) === normalizedQueryKey
    );

  const dropdownItems = React.useMemo(() => {
    const items = suggestionOptions.map((tag) => ({
      type: 'option',
      tag,
      key: `option-${tag.value}`,
    }));

    if (canCreate) {
      items.unshift({
        type: 'create',
        tag: createTagOption(normalizedQuery),
        key: `create-${normalizedQueryKey}`,
      });
    }

    return items;
  }, [canCreate, normalizedQuery, normalizedQueryKey, suggestionOptions]);

  React.useEffect(() => {
    if (highlightedIndex > dropdownItems.length - 1) {
      setHighlightedIndex(-1);
    }
  }, [dropdownItems.length, highlightedIndex]);

  const commitSelection = React.useCallback(
    (nextTag) => {
      const normalizedTag = createTagOption(
        normalizeTagName(nextTag?.label ?? nextTag?.value)
      );

      if (!normalizedTag.label) {
        return;
      }

      const alreadySelected = selectedTags.some(
        (tag) => getTagKey(tag.value || tag.label) === getTagKey(normalizedTag.label)
      );

      if (alreadySelected) {
        setQuery('');
        return;
      }

      onChange?.([...selectedTags, normalizedTag]);
      setQuery('');
      setIsOpen(true);
      setHighlightedIndex(-1);
      window.requestAnimationFrame(() => inputRef.current?.focus());
    },
    [onChange, selectedTags]
  );

  const removeTag = React.useCallback(
    (tagToRemove) => {
      onChange?.(
        selectedTags.filter(
          (tag) =>
            getTagKey(tag.value || tag.label) !==
            getTagKey(tagToRemove.value || tagToRemove.label)
        )
      );
    },
    [onChange, selectedTags]
  );

  const clearAll = React.useCallback(() => {
    onChange?.([]);
    setQuery('');
  }, [onChange]);

  const handleKeyDown = (event) => {
    if (disabled) {
      return;
    }

    if (event.key === 'Backspace' && !query && selectedTags.length > 0) {
      event.preventDefault();
      removeTag(selectedTags[selectedTags.length - 1]);
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex((current) =>
        dropdownItems.length === 0
          ? -1
          : current < 0
            ? 0
            : (current + 1) % dropdownItems.length
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex((current) =>
        dropdownItems.length === 0
          ? -1
          : current < 0
            ? dropdownItems.length - 1
            : (current - 1 + dropdownItems.length) % dropdownItems.length
      );
      return;
    }

    if ((event.key === 'Enter' || event.key === 'Tab') && normalizedQuery) {
      const activeItem = dropdownItems[highlightedIndex];

      if (activeItem) {
        event.preventDefault();
        commitSelection(activeItem.tag);
        return;
      }

      if (canCreate) {
        event.preventDefault();
        commitSelection(createTagOption(normalizedQuery));
      }
    }
  };

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div
        className={cn(
          'flex min-h-11 w-full flex-wrap items-center gap-2 rounded-xl border border-input bg-background px-3 py-2 shadow-none',
          'transition-[border-color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/35',
          controlClassName,
          disabled && 'cursor-not-allowed opacity-70'
        )}
        onClick={() => {
          if (disabled) {
            return;
          }

          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        {selectedTags.map((tag) => (
          <span key={tag.value} className={TAG_BADGE_CLASS_NAME}>
            <span className="max-w-[160px] truncate">{tag.label}</span>
            <button
              type="button"
              className={cn(
                'inline-flex h-6 w-6 items-center justify-center rounded-full border border-zinc-700/80',
                'bg-transparent text-zinc-400 shadow-none transition-colors',
                'hover:border-zinc-600 hover:bg-zinc-900/70 hover:text-zinc-100',
                'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35',
                'dark:border-zinc-700 dark:bg-transparent dark:text-zinc-400 dark:hover:border-zinc-600 dark:hover:bg-zinc-900/70'
              )}
              aria-label={`Remove ${tag.label}`}
              onClick={(event) => {
                event.stopPropagation();
                removeTag(tag);
              }}
              disabled={disabled}
            >
              <X className="h-5 w-5 stroke-[2.6]" />
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          type="text"
          value={query}
          disabled={disabled}
          onFocus={() => setIsOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? placeholder : searchPlaceholder}
          className="min-w-[120px] flex-1 border-0 bg-transparent px-0 py-1 text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
        />

        {selectedTags.length > 0 ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              clearAll();
            }}
            className={cn(
              'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-zinc-700/80',
              'bg-transparent text-zinc-400 shadow-none transition-colors',
              'hover:border-zinc-600 hover:bg-zinc-900/70 hover:text-zinc-100',
              'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35',
              'dark:border-zinc-700 dark:bg-transparent dark:text-zinc-400 dark:hover:border-zinc-600 dark:hover:bg-zinc-900/70'
            )}
            aria-label="Clear all selected tags"
            disabled={disabled}
          >
            <X className="h-3.5 w-3.5 stroke-[2.5]" />
          </button>
        ) : null}
      </div>

      {isOpen && !disabled ? (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-border bg-popover shadow-md">
          <div className="border-b border-border/60 px-3 py-2.5">
            <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
              <Search className="h-3.5 w-3.5" />
              <span>
                {normalizedQuery
                  ? `Matching tags for "${normalizedQuery}"`
                  : 'Browse existing tags'}
              </span>
              {loading ? (
                <LoaderCircle className="ml-auto h-3.5 w-3.5 animate-spin" />
              ) : null}
            </div>
          </div>

          <div
            role="listbox"
            style={{ maxHeight }}
            className="themed-slim-scrollbar overflow-y-auto p-1.5"
          >
            {dropdownItems.length === 0 ? (
              <div className="px-3 py-3 text-[13px] text-muted-foreground">
                {loading ? 'Searching tags...' : emptyMessage}
              </div>
            ) : (
              dropdownItems.map((item, index) => {
                const isActive = index === highlightedIndex;
                const isCreate = item.type === 'create';

                return (
                  <button
                    key={item.key}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={cn(
                          'flex w-full items-center gap-2 rounded-lg border border-transparent px-3 py-2.5 text-left text-[13px] transition-colors bg-transparent',
                          isActive
                            ? 'border-border bg-accent text-accent-foreground'
                            : 'text-muted-foreground hover:border-border hover:bg-accent hover:text-accent-foreground'
                    )}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseLeave={() => setHighlightedIndex(-1)}
                    onClick={() => commitSelection(item.tag)}
                  >
                    <span
                      className={cn(
                        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md border',
                        isCreate
                          ? 'border-dashed border-primary/60 bg-primary/5 text-primary dark:bg-primary/10'
                              : 'border-border bg-transparent text-muted-foreground'
                      )}
                    >
                      {isCreate ? (
                        <span className="text-sm font-semibold leading-none">+</span>
                      ) : (
                        <Plus className="h-3.5 w-3.5 stroke-[2.5]" />
                      )}
                    </span>

                    <span className="min-w-0 flex-1 truncate">
                      {isCreate
                        ? `${createMessage} "${item.tag.label}"`
                        : item.tag.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TagInput;
